import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InventoryHistoryData } from '@app/shared/interfaces/inventory-data';
import { AssetManagementService } from '../../asset-management-service';
import { InventoryTableService } from '@app/pages/asset-management/asset-management-table/inventory-table.service';
import { FileUploadService } from '@services/file-upload.service';
import { FileDownloadService } from '@services/file-download.service';
import { ToastrService } from 'ngx-toastr';
import { LOCATION_OPTIONS } from '../../my-request/my-request-form/my-request-form.constants';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { size } from 'lodash';
import { AuthService } from '@core/auth.service';

interface DialogData {
  mode: 'add' | 'view' | 'add-invoice' | 'view-invoice' | 'issue-new-item' | 'bulk-issue-upload';
  rowData: any;
  inventoryHistoryData?: InventoryHistoryData[];
  isOrder?: boolean;
  isEdit?: boolean;
}

interface DetailConfig {
  label: string;
  controlName: string;
}

interface InventoryHistoryRow {
  type: 'Procured' | 'Issued';
  date: string | null;
  quantity: number | string | null;
  by: string | null;
}

@Component({
  selector: 'app-inventory-forms',
  templateUrl: './inventory-forms.component.html',
  styleUrl: './inventory-forms.component.scss',
})
export class InventoryFormsComponent implements OnInit {
  selectedLocation: any | null = null;
  selectedFilterLocation: string = 'Hyderabad';

  editForm!: FormGroup;
  addNewForm!: FormGroup;
  invoiceForm!: FormGroup;
  viewForm!: FormGroup;
  addInventoryForm!: FormGroup;

  Locations: any[] = [];
  deliveryStatuses: { label: string; value: string }[] = [];
  isOrderMode: boolean = false;
  dropdownKey = 0;
  isEditInventory!: boolean;
  inventoryHistory: InventoryHistoryData[] = [];
  tableHistory: any[] = [];
  locationOptions: any[] = LOCATION_OPTIONS;
  isInvoiceAddMode = false;
  isInvoiceViewMode = false;

  uploadedInvoice: File | null = null;
  invoicePreview: string | null = null;

  uploadedFile: File | null = null;
  fileError: string | null = null;
  isViewMode!: boolean;
  isBulkIssueUploadMode: boolean = false;
  bulkUploadFile: File | null = null;
  bulkUploadFileError: string | null = null;
  safeInvoiceUrl: SafeResourceUrl | null = null;

  allProductOptions: any[] = [];
  shirtSelection: any;

  private currentRecordId: string | number | null = null;

  public viewDetailsConfig: DetailConfig[] = [
    { label: 'Procured Quantity', controlName: 'procuredQuantity' },
    { label: 'Date Of Issue', controlName: 'dateOfIssue' },
    { label: 'Date Of Procurement', controlName: 'dateOfProcurement' },
    { label: 'Issued Quantity', controlName: 'issuedQuantity' },
    { label: 'Issued By', controlName: 'issuedCreatedBy' },
    { label: 'Procured By', controlName: 'procuredCreatedBy' },
  ];
  subProductItems: any;
  allItemsArray: any[] = [];
  issueSizeOptions: any[] = [];
  categoryOptions: any[] = [];
  categoryGroups: any[] = [];
  productOptionsForCategory: any[] = [];

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private dialogRef: MatDialogRef<InventoryFormsComponent>,
    private assetManagementService: AssetManagementService,
    private inventoryTableService: InventoryTableService,
    private fileUploadService: FileUploadService,
    private fileDownloadService: FileDownloadService,
    private toasterService: ToastrService,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.initializeEditForm();
    this.initializeAddNewForm();
    this.initializeAddInventoryForm();

    if (this.dialogData.mode === 'add' || this.dialogData.mode === 'issue-new-item') {
      this.loadCategoryOptions();
    }

    if (this.dialogData.mode === 'issue-new-item') {
      if (this.dialogData.isEdit) {
        this.addInventoryForm.get('category')?.disable();
        this.addInventoryForm.get('itemName')?.disable();
        this.addInventoryForm.get('location')?.disable();
        this.addInventoryForm.get('issuedTo')?.disable();
      }
    }
    this.isViewMode = this.dialogData.mode === 'view';
    this.isInvoiceAddMode = this.dialogData.mode === 'add-invoice';
    this.isInvoiceViewMode = this.dialogData.mode === 'view-invoice';
    this.isBulkIssueUploadMode = this.dialogData.mode === 'bulk-issue-upload';

    if (this.isViewMode) {
      this.loadViewData(
        this.dialogData.rowData,
        this.dialogData.inventoryHistoryData,
      );
    } else {
      this.resetAllStates();
    }

    if (this.isInvoiceViewMode && this.dialogData.rowData?.procuredOrderId) {
      this.loadInvoicePreviewById(this.dialogData.rowData.procuredOrderId);
    }
  }

  private toTitleCase(str: string): string {
    if (!str) return '';
    return str
      .toUpperCase()
      .split(' ')
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  loadInvoicePreviewById(procuredOrderId: number): void {
    this.assetManagementService
      .downloadInvoicePreviewImage(procuredOrderId)
      .subscribe({
        next: (blob) => {
          const imageUrl = URL.createObjectURL(blob);
          this.safeInvoiceUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(imageUrl);
        },
        error: () => {
          this.toasterService.error('Unable to load invoice preview');
        },
      });
  }

  private loadCategoryOptions(): void {
    this.assetManagementService.getGroupedByCategory().subscribe({
      next: (response: any) => {
        this.categoryGroups = response;
        this.categoryOptions = response.map((group: any) => ({
          label: group.categoryName?.trim(),
          value: group.categoryName,
        }));

        // Pre-fill form when editing an existing row
        const row = this.dialogData.rowData;
        if (row && row.category) {
          const matchedGroup = this.categoryGroups.find(
            (group: any) => group.categoryName === row.category,
          );
          if (matchedGroup) {
            this.productOptionsForCategory = (matchedGroup.products || []).map(
              (product: string) => ({ label: product?.trim(), value: product }),
            );
          }
          // Defer patch until after Angular propagates new options to child dropdowns
          setTimeout(() => {
            this.addInventoryForm.patchValue({
              category: row.category,
              itemName: row.product ?? row.itemName ?? row.productName ?? null,
              location: row.location ?? null,
              issuedTo: row.issuedTo ?? null,
              purpose: row.purpose ?? null,
              issuedQuantity: row.quantity ?? row.issuedQuantity ?? 1,
              dateOfIssue: row.dateOfIssue ? new Date(row.dateOfIssue) : new Date(),
            });
          }, 0);
        }
      },
      error: (err) => {
        this.toasterService.error('Failed to load categories');
        this.categoryOptions = [];
        this.categoryGroups = [];
      },
    });
  }

  handleCategoryChange(event: any): void {
    const selectedCategory = event?.value ?? event;

    this.productOptionsForCategory = [];

    const activeForm =
      this.dialogData.mode === 'add' ? this.addNewForm : this.addInventoryForm;
    activeForm.patchValue({ itemName: null });

    const matchedGroup = this.categoryGroups.find(
      (group: any) => group.categoryName === selectedCategory,
    );

    if (matchedGroup) {
      this.productOptionsForCategory = (matchedGroup.products || []).map(
        (product: string) => ({
          label: product?.trim(),
          value: product,
        }),
      );
    }
  }

  private loadViewData(row: any, historyList: any): void {
    this.inventoryHistory = historyList ?? [];

    this.tableHistory = historyList;

    const toDateOnly = (val?: string | null) =>
      val ? val.split('T')[0] : null;

    this.viewForm.patchValue({
      procuredQuantity: row?.procuredQuantity ?? null,
      dateOfProcurement: toDateOnly(row?.dateOfProcurement) ?? null,
      issuedQuantity: row?.issuedQuantity ?? null,
      dateOfIssue: toDateOnly(row?.dateOfIssue) ?? null,
      issuedCreatedBy: row?.issuedCreatedBy ?? null,
      procuredCreatedBy: row?.procuredCreatedBy ?? null,
    });
  }

  private loadAddNewData(data: any): void {
    if (!data) {
      return;
    }
    this.currentRecordId = data.procuredOrderId;

    this.addNewForm.patchValue({
      category: data.category,
      itemName: data.itemName,
      procuredQuantity: data.procuredQuantity,
      supplierName: data.supplierName,
      location: data.location,
      dateOfProcurement: data.dateOfProcurement,
      ordersAttachmentUrl: data.ordersAttachmentUrl,
    });
  }

  private resetAllStates(): void {
    this.addNewForm.reset();

    this.selectedLocation = null;
    this.uploadedFile = null;
    this.fileError = null;

    this.addNewForm.get('location')?.setValue(null);
    this.addNewForm.get('dateOfProcurement')?.setValue(new Date());
  }

  initializeEditForm(): void {
    this.invoiceForm = this.fb.group({
      ordersAttachmentUrl: [null, Validators.required],
    });

    this.viewForm = this.fb.group({
      procuredQuantity: [null],
      dateOfProcurement: [null],
      issuedQuantity: [null],
      dateOfIssue: [null],
      issuedCreatedBy: [null],
      procuredCreatedBy: [null],
    });
  }

  initializeAddNewForm(): void {
    this.addNewForm = this.fb.group({
      category: [null, Validators.required],
      itemName: [null, Validators.required],
      procuredQuantity: [
        null,
        [Validators.required, Validators.pattern('^\\d+$')],
      ],
      supplierName: [null, Validators.required],
      location: [null, Validators.required],
      dateOfProcurement: [new Date(), Validators.required],
      ordersAttachmentUrl: [null],
    });
  }

  initializeAddInventoryForm(): void {
    this.addInventoryForm = this.fb.group({
      location: [null, Validators.required],
      itemName: [null, Validators.required],
      category: [null, Validators.required],
      issuedQuantity: [
        1,
        [Validators.required, Validators.min(1), Validators.pattern('^\\d+$')],
      ],
      issuedTo: [null, Validators.required],
      dateOfIssue: [new Date(), Validators.required],
      purpose: [null, Validators.required],
    });
  }

  private formatDateOnly(dateValue: any): string | null {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  submitInvoice() {
    const url = this.invoiceForm.get('ordersAttachmentUrl')?.value;
    const orderId = this.dialogData.rowData.procuredOrderId;

    this.assetManagementService
      .updateOrdersAttachmentUrl(orderId, url)
      .subscribe(() => {
        this.toasterService.success('Invoice attachment updated!');
        // this.inventoryTableService.loadInitialData('inventory-order');
        this.dialogRef.close();
      });
  }

  onInvoiceSelected(file: File) {
    const allowedTypes = ['application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.fileError = 'Only PDF files are allowed.';
      this.uploadedInvoice = null;
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      this.fileError = 'File size cannot exceed 3 MB.';
      this.uploadedInvoice = null;
      return;
    }

    this.fileError = null;
    this.uploadedInvoice = file;

    const reader = new FileReader();
    reader.onload = () => (this.invoicePreview = reader.result as string);
    reader.readAsDataURL(file);

    this.fileUploadService.uploadFile(file, 'orders_attachment').subscribe({
      next: (res) => {
        if (res?.filePath) {
          this.invoiceForm.get('ordersAttachmentUrl')?.setValue(res.filePath);
          this.addNewForm.get('ordersAttachmentUrl')?.setValue(res.filePath);
        }
      },
      error: (err) => {
        this.toasterService.error('Failed to upload invoice attachment');
      },
    });
  }

  downloadInvoice() {
    const procuredOrderId = this.dialogData.rowData?.procuredOrderId;
    const itemName = this.dialogData.rowData?.itemName;
    const url = this.dialogData.rowData?.ordersAttachmentUrl;
    if (!procuredOrderId || !url) {
      this.toasterService.error('No invoice to download.');
      return;
    }
    const sanitizedItemName = itemName.replace(/[\s\W-]+/g, '_');
    const fileName = `invoice_${sanitizedItemName}.pdf`;
    this.fileDownloadService.downloadFile(url, fileName);
  }

  onInvoiceRemoved() {
    this.uploadedInvoice = null;
    this.invoicePreview = null;
  }

  onBulkFileSelected(file: File): void {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    if (!allowedTypes.includes(file.type)) {
      this.bulkUploadFileError = 'Only Excel (.xls, .xlsx) or CSV files are allowed.';
      this.bulkUploadFile = null;
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.bulkUploadFileError = 'File size cannot exceed 5 MB.';
      this.bulkUploadFile = null;
      return;
    }
    this.bulkUploadFileError = null;
    this.bulkUploadFile = file;
  }

  onBulkFileRemoved(): void {
    this.bulkUploadFile = null;
    this.bulkUploadFileError = null;
  }

  isBulkUploading: boolean = false;

  // onBulkUploadSubmit(): void {
  //   if (!this.bulkUploadFile) {
  //     this.bulkUploadFileError = 'Please select a file to upload.';
  //     return;
  //   }

  //   this.isBulkUploading = true;
  //   this.assetManagementService
  //     .bulkIssueUpload(this.bulkUploadFile.name)
  //     .subscribe({
  //       next: () => {
  //         this.isBulkUploading = false;
  //         this.toasterService.success('Bulk issue file uploaded!');
  //         this.dialogRef.close(true);
  //       },
  //       error: () => {
  //         this.isBulkUploading = false;
  //         this.bulkUploadFileError =
  //           'Failed to upload file. Please try again.';
  //       },
  //     });
  // }

  onBulkUploadSubmit(): void {
  if (!this.bulkUploadFile) {
    this.bulkUploadFileError = 'Please select a file to upload.';
    return;
  }

  this.isBulkUploading = true;
  const upload$ = this.dialogData.isOrder
    ? this.assetManagementService.bulkProcuredItemsUpload(this.bulkUploadFile)
    : this.assetManagementService.bulkIssueUpload(this.bulkUploadFile);

  upload$.subscribe({
      next: () => {
        this.isBulkUploading = false;
        this.toasterService.success(
          this.dialogData.isOrder
            ? 'Bulk procured items file uploaded!'
            : 'Bulk issue file uploaded!',
        );
        this.dialogRef.close(true);
      },
      error: () => {
        this.isBulkUploading = false;
        this.bulkUploadFileError =
          'Failed to upload file. Please try again.';
      },
    });
}

  onSubmit(): void {
    const activeForm =
      this.dialogData.mode === 'issue-new-item'
        ? this.addInventoryForm
        : this.isEditInventory
          ? this.editForm
          : this.addNewForm;

    if (this.dialogData.mode === 'add') {
      const procuredQuantityString = activeForm.get('procuredQuantity')?.value;
      const procuredQuantityNumber = Number(procuredQuantityString);

      const fileAttachmentValue = activeForm.get('ordersAttachmentUrl')?.value;

      const payload: any = {
        categoryName: activeForm.get('category')?.value,
        productName: activeForm.get('itemName')?.value,
        quantity: procuredQuantityNumber,
        dateOfPurchase: this.formatDateOnly(
          activeForm.get('dateOfProcurement')?.value,
        ),
        location: activeForm.get('location')?.value,
        supplierName: activeForm.get('supplierName')?.value,
        fileAttachment: fileAttachmentValue,
        createdBy: [
          this.authService.userProfile?.firstName,
          this.authService.userProfile?.lastName,
        ]
          .filter(Boolean)
          .join(' '),
      };

      this.assetManagementService.addNewInventoryItem(payload).subscribe({
        next: (response) => {
          this.toasterService.success('New Record Added');
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.toasterService.error('Failed to add record');
        },
      });

      return;
    }

    if (this.dialogData.mode === 'issue-new-item') {
      const issuedQuantityValue = activeForm.get('issuedQuantity')?.value;

      if (isNaN(issuedQuantityValue) || issuedQuantityValue <= 0) {
        this.toasterService.error(
          'Issued quantity must be a valid number greater than 0',
        );
        activeForm.get('issuedQuantity')?.setErrors({ invalid: true });
        return;
      }

      const isEdit = this.dialogData.isEdit;
      const row = this.dialogData.rowData;

      const payload: any = {
        categoryName: activeForm.get('category')?.value,
        productName: activeForm.get('itemName')?.value,
        quantity: issuedQuantityValue,
        location: activeForm.get('location')?.value,
        issuedTo: activeForm.get('issuedTo')?.value,
        issueDate: this.formatDateOnly(activeForm.get('dateOfIssue')?.value),
        purpose: activeForm.get('purpose')?.value,
        createdBy: [
          this.authService.userProfile?.firstName,
          this.authService.userProfile?.lastName,
        ]
          .filter(Boolean)
          .join(' '),
      };

      if (isEdit) {
        payload.itemIssueId = row?.itemIssueId ?? row?.issuedOrderId ?? row?.itemId;
      }

      const apiCall = isEdit
        ? this.assetManagementService.editIssueOrder(payload)
        : this.assetManagementService.updateIssueOrder(payload);

      apiCall.subscribe({
        next: () => {
          this.toasterService.success(isEdit ? 'Record updated!' : 'Record created!');
          this.dialogRef.close(true);
        },
        error: () => {
          this.toasterService.error(isEdit ? 'Failed to update record' : 'Failed to create record');
        },
      });

      return;
    }

    this.dialogRef.close({
      formData: activeForm.value,
      id: this.currentRecordId,
    });
  }

  cancelEdit(): void {
    this.resetAllStates();
    this.dialogRef.close();
  }

  closeForm(): void {
    this.resetAllStates();
    this.dialogRef.close();
  }
}